/**
 * EA Product Gallery
 *
 * Custom element that renders a variant-keyed product image gallery.
 * Replaces Flow's <product-media> + Swiper for products with
 * custom.variant_gallery_v2 populated.
 *
 * v4 update (5/28/26):
 *  - Removed drag/swipe slide navigation entirely (desktop + mobile)
 *  - Click/tap on main image now opens a lightbox showing the larger image
 *  - Lightbox supports prev/next, ESC / backdrop click / close button to dismiss
 *  - Slide navigation outside the lightbox: thumbnails (always) +
 *    arrows (desktop only) + keyboard left/right (when main area has focus)
 *  - Lightbox is a singleton lazily appended to <body> on first open
 *
 * v3 (deprecated): drag/swipe slide nav with slide transform.
 * v2: relaxed sectionId matching so quick-view variant changes
 * are honored even when Flow emits a different sectionId than the
 * gallery's data-section-id.
 *
 * Author: Kailah Korsh (EA D2C)
 */
(function () {
  'use strict';

  // ---------------------------------------------------------
  // Debug helper. ?ea-debug=1 turns on verbose logging.
  // ---------------------------------------------------------
  var DEBUG = (function () {
    try {
      return new URLSearchParams(window.location.search).get('ea-debug') === '1';
    } catch (e) {
      return false;
    }
  })();

  function log() {
    if (!DEBUG) return;
    var args = ['[ea-product-gallery]'].concat(Array.prototype.slice.call(arguments));
    console.log.apply(console, args);
  }

  function warn() {
    var args = ['[ea-product-gallery]'].concat(Array.prototype.slice.call(arguments));
    console.warn.apply(console, args);
  }

  // ---------------------------------------------------------
  // Wait for Flow's eventBus before subscribing to variant changes.
  // ---------------------------------------------------------
  function whenEventBusReady(cb) {
    if (window.eventBus && typeof window.eventBus.on === 'function') {
      cb();
      return;
    }
    var attempts = 0;
    var iv = setInterval(function () {
      attempts++;
      if (window.eventBus && typeof window.eventBus.on === 'function') {
        clearInterval(iv);
        cb();
      } else if (attempts > 50) {
        clearInterval(iv);
        warn('eventBus never became available; gallery will work but variant changes from sibling selectors will not be picked up.');
        cb();
      }
    }, 100);
  }

  // ---------------------------------------------------------
  // Already defined? bail (handles re-injection in quick view scenarios)
  // ---------------------------------------------------------
  if (window.customElements && customElements.get('ea-product-gallery')) {
    return;
  }

  // =========================================================
  // LIGHTBOX SINGLETON
  // ---------------------------------------------------------
  // One lightbox shared across all <ea-product-gallery> instances on
  // the page. Lazy-created on first open. When opened, it reads the
  // active slide from the gallery instance that triggered it. Prev/next
  // inside the lightbox delegate back to that gallery's slide-nav
  // methods, so closing the lightbox leaves the gallery on whichever
  // slide you ended on — they stay in sync.
  // =========================================================
  var _lightbox = null;

  function getLightbox() {
    if (!_lightbox) _lightbox = createLightbox();
    return _lightbox;
  }

  function createLightbox() {
    var el = document.createElement('div');
    el.className = 'ea-lightbox';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Image viewer');
    el.setAttribute('aria-hidden', 'true');
    el.hidden = true;
    el.innerHTML = [
      '<div class="ea-lightbox__backdrop" data-ea-lb-backdrop></div>',
      '<button type="button" class="ea-lightbox__zoom" data-ea-lb-zoom aria-label="Zoom in">',
        '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">',
          '<circle cx="11" cy="11" r="7"/>',
          '<line x1="16.5" y1="16.5" x2="21" y2="21"/>',
          '<line x1="8" y1="11" x2="14" y2="11"/>',
          '<line x1="11" y1="8" x2="11" y2="14"/>',
        '</svg>',
      '</button>',
      '<button type="button" class="ea-lightbox__close" data-ea-lb-close aria-label="Close image viewer">',
        '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">',
          '<line x1="6" y1="6" x2="18" y2="18"/><line x1="6" y1="18" x2="18" y2="6"/>',
        '</svg>',
      '</button>',
      '<button type="button" class="ea-lightbox__nav ea-lightbox__nav--prev" data-ea-lb-prev aria-label="Previous image" hidden>',
        '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
          '<polyline points="15 18 9 12 15 6"/>',
        '</svg>',
      '</button>',
      '<button type="button" class="ea-lightbox__nav ea-lightbox__nav--next" data-ea-lb-next aria-label="Next image" hidden>',
        '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
          '<polyline points="9 18 15 12 9 6"/>',
        '</svg>',
      '</button>',
      '<div class="ea-lightbox__stage" data-ea-lb-stage>',
        '<img class="ea-lightbox__image" data-ea-lb-image alt=""/>',
      '</div>'
    ].join('');

    document.body.appendChild(el);

    var imgEl = el.querySelector('[data-ea-lb-image]');
    var stageEl = el.querySelector('[data-ea-lb-stage]');
    var prevBtn = el.querySelector('[data-ea-lb-prev]');
    var nextBtn = el.querySelector('[data-ea-lb-next]');
    var closeBtn = el.querySelector('[data-ea-lb-close]');
    var zoomBtn = el.querySelector('[data-ea-lb-zoom]');
    var backdrop = el.querySelector('[data-ea-lb-backdrop]');

    var state = {
      gallery: null,
      previouslyFocused: null
    };

    // ----- Zoom / pan state -----
    // Single shared transform applied to imgEl: translate(tx,ty) scale(scale).
    // pinch/pan use raw pointer events; double-tap and wheel use applyScale-
    // AroundPoint so the pivot stays under the cursor/fingers.
    var MIN_SCALE = 1;
    var MAX_SCALE = 4;
    var DOUBLE_TAP_ZOOM = 2.5;
    var DOUBLE_TAP_MS = 300;
    var DOUBLE_TAP_DIST = 30;
    var WHEEL_STEP = 1.15;

    var scale = 1;
    var tx = 0;
    var ty = 0;
    var pointers = new Map();
    var lastPinchDistance = null;
    var lastPanX = null;
    var lastPanY = null;
    var lastTapTime = 0;
    var lastTapX = 0;
    var lastTapY = 0;
    // Set true the moment a multi-touch gesture starts so the click event
    // fired by the last finger lifting doesn't accidentally trigger a
    // double-tap zoom toggle.
    var suppressNextClick = false;

    function clampNum(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

    function applyTransform() {
      imgEl.style.transform = 'translate(' + tx + 'px, ' + ty + 'px) scale(' + scale + ')';
      var zoomed = scale > 1;
      imgEl.classList.toggle('is-zoomed', zoomed);
      if (zoomBtn) {
        zoomBtn.classList.toggle('is-active', zoomed);
        zoomBtn.setAttribute('aria-label', zoomed ? 'Reset zoom' : 'Zoom in');
      }
    }

    function clampTranslation() {
      var rect = stageEl.getBoundingClientRect();
      var maxX = (rect.width * (scale - 1)) / 2;
      var maxY = (rect.height * (scale - 1)) / 2;
      tx = clampNum(tx, -maxX, maxX);
      ty = clampNum(ty, -maxY, maxY);
    }

    function applyScaleAroundPoint(newScale, pivotX, pivotY, animate) {
      newScale = clampNum(newScale, MIN_SCALE, MAX_SCALE);
      if (newScale === scale) return;
      var rect = stageEl.getBoundingClientRect();
      var cx = rect.left + rect.width / 2;
      var cy = rect.top + rect.height / 2;
      var dx = pivotX - cx;
      var dy = pivotY - cy;
      var ratio = newScale / scale;
      tx = dx * (1 - ratio) + tx * ratio;
      ty = dy * (1 - ratio) + ty * ratio;
      scale = newScale;
      if (scale <= MIN_SCALE) {
        scale = MIN_SCALE;
        tx = ty = 0;
      } else {
        clampTranslation();
      }
      if (animate) {
        imgEl.classList.add('is-animating');
        setTimeout(function () { imgEl.classList.remove('is-animating'); }, 220);
      }
      applyTransform();
    }

    function resetZoom() {
      scale = MIN_SCALE;
      tx = 0;
      ty = 0;
      pointers.clear();
      lastPinchDistance = null;
      lastPanX = null;
      lastPanY = null;
      lastTapTime = 0;
      suppressNextClick = false;
      imgEl.classList.remove('is-zoomed');
      imgEl.classList.remove('is-animating');
      imgEl.style.transform = '';
      if (zoomBtn) {
        zoomBtn.classList.remove('is-active');
        zoomBtn.setAttribute('aria-label', 'Zoom in');
      }
    }

    function onImgPointerDown(e) {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      try { imgEl.setPointerCapture(e.pointerId); } catch (err) {}

      if (pointers.size === 2) {
        var vals = Array.from(pointers.values());
        lastPinchDistance = Math.hypot(vals[1].x - vals[0].x, vals[1].y - vals[0].y);
        lastPanX = lastPanY = null;
        suppressNextClick = true;
      } else if (pointers.size === 1 && scale > 1) {
        lastPanX = e.clientX;
        lastPanY = e.clientY;
      }
    }

    function onImgPointerMove(e) {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointers.size === 2) {
        var vals = Array.from(pointers.values());
        var dist = Math.hypot(vals[1].x - vals[0].x, vals[1].y - vals[0].y);
        var midX = (vals[0].x + vals[1].x) / 2;
        var midY = (vals[0].y + vals[1].y) / 2;
        if (lastPinchDistance != null && lastPinchDistance > 0) {
          var factor = dist / lastPinchDistance;
          var newScale = clampNum(scale * factor, MIN_SCALE, MAX_SCALE);
          applyScaleAroundPoint(newScale, midX, midY, false);
        }
        lastPinchDistance = dist;
        if (e.cancelable) e.preventDefault();
      } else if (pointers.size === 1 && scale > 1 && lastPanX != null) {
        tx += e.clientX - lastPanX;
        ty += e.clientY - lastPanY;
        lastPanX = e.clientX;
        lastPanY = e.clientY;
        clampTranslation();
        applyTransform();
        if (e.cancelable) e.preventDefault();
      }
    }

    function onImgPointerUp(e) {
      if (!pointers.has(e.pointerId)) return;
      pointers.delete(e.pointerId);
      try { imgEl.releasePointerCapture(e.pointerId); } catch (err) {}

      if (pointers.size < 2) lastPinchDistance = null;
      if (pointers.size === 0) {
        lastPanX = null;
        lastPanY = null;
      } else if (pointers.size === 1 && scale > 1) {
        // Resume single-finger pan with whichever finger is still down
        var vals = Array.from(pointers.values());
        lastPanX = vals[0].x;
        lastPanY = vals[0].y;
      }
    }

    function onImgWheel(e) {
      e.preventDefault();
      var factor = e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
      var newScale = clampNum(scale * factor, MIN_SCALE, MAX_SCALE);
      applyScaleAroundPoint(newScale, e.clientX, e.clientY, false);
    }

    function onImgClick(e) {
      e.stopPropagation();
      if (suppressNextClick) {
        suppressNextClick = false;
        return;
      }
      var now = Date.now();
      var dist = Math.hypot(e.clientX - lastTapX, e.clientY - lastTapY);
      if (now - lastTapTime < DOUBLE_TAP_MS && dist < DOUBLE_TAP_DIST) {
        // Double-tap → toggle zoom
        if (scale > 1) {
          applyScaleAroundPoint(MIN_SCALE, e.clientX, e.clientY, true);
        } else {
          applyScaleAroundPoint(DOUBLE_TAP_ZOOM, e.clientX, e.clientY, true);
        }
        lastTapTime = 0;
      } else {
        lastTapTime = now;
        lastTapX = e.clientX;
        lastTapY = e.clientY;
      }
    }

    function refresh() {
      if (!state.gallery) return;
      var slide = state.gallery._getActiveSlide();
      if (!slide) return;
      var sourceImg = slide.querySelector('.ea-gallery__image');
      if (!sourceImg) return;
      resetZoom();
      var fullSrc = sourceImg.getAttribute('data-ea-img-full') || sourceImg.src;
      imgEl.src = fullSrc;
      imgEl.alt = sourceImg.alt || '';

      var hasMultiple = state.gallery._getCurrentSlideIndex().list.length > 1;
      prevBtn.hidden = !hasMultiple;
      nextBtn.hidden = !hasMultiple;
    }

    function open(gallery) {
      state.gallery = gallery;
      state.previouslyFocused = document.activeElement;
      refresh();
      el.hidden = false;
      // Force reflow then add visible class so the opacity transition runs
      void el.offsetWidth;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('ea-lightbox-open');
      document.addEventListener('keydown', onKeyDown);
      closeBtn.focus();
      log('Lightbox opened for gallery', gallery);
    }

    function close() {
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('ea-lightbox-open');
      document.removeEventListener('keydown', onKeyDown);
      // Hide after the opacity transition completes so screen readers
      // and focus management treat it as truly removed.
      setTimeout(function () {
        if (!el.classList.contains('is-open')) {
          el.hidden = true;
        }
      }, 220);

      var prev = state.previouslyFocused;
      var gallery = state.gallery;
      state.gallery = null;
      state.previouslyFocused = null;
      if (prev && typeof prev.focus === 'function') {
        try { prev.focus(); } catch (e) {}
      } else if (gallery && gallery._mainArea) {
        try { gallery._mainArea.focus(); } catch (e) {}
      }
      log('Lightbox closed');
    }

    function navigate(direction) {
      if (!state.gallery) return;
      if (direction === 'next') state.gallery._goNext();
      else state.gallery._goPrev();
      refresh();
    }

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigate('next');
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigate('prev');
      }
    }

    closeBtn.addEventListener('click', close);
    backdrop.addEventListener('click', close);
    prevBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      navigate('prev');
    });
    nextBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      navigate('next');
    });
    // Magnifier toggles between 1x and DOUBLE_TAP_ZOOM, pivoting on image center
    zoomBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      var rect = stageEl.getBoundingClientRect();
      var cx = rect.left + rect.width / 2;
      var cy = rect.top + rect.height / 2;
      if (scale > 1) {
        applyScaleAroundPoint(MIN_SCALE, cx, cy, true);
      } else {
        applyScaleAroundPoint(DOUBLE_TAP_ZOOM, cx, cy, true);
      }
    });
    // Image interaction:
    //  - click → double-tap zoom toggle (stopPropagation prevents backdrop close)
    //  - pointerdown/move/up → pinch zoom + pan
    //  - wheel → desktop zoom centered on cursor
    imgEl.addEventListener('click', onImgClick);
    imgEl.addEventListener('pointerdown', onImgPointerDown);
    imgEl.addEventListener('pointermove', onImgPointerMove);
    imgEl.addEventListener('pointerup', onImgPointerUp);
    imgEl.addEventListener('pointercancel', onImgPointerUp);
    imgEl.addEventListener('wheel', onImgWheel, { passive: false });

    return {
      el: el,
      open: open,
      close: close,
      refresh: refresh
    };
  }

  // =========================================================
  // CUSTOM ELEMENT
  // =========================================================
  var EAProductGallery = function () {
    return Reflect.construct(HTMLElement, [], EAProductGallery);
  };
  EAProductGallery.prototype = Object.create(HTMLElement.prototype);
  EAProductGallery.prototype.constructor = EAProductGallery;
  Object.setPrototypeOf(EAProductGallery, HTMLElement);

  // ---- Lifecycle ----
  EAProductGallery.prototype.connectedCallback = function () {
    if (this._initialized) return;
    this._initialized = true;

    this._slidesContainer = this.querySelector('[data-ea-slides]');
    this._thumbsContainer = this.querySelector('[data-ea-thumbs]');
    this._prevBtn = this.querySelector('[data-ea-prev]');
    this._nextBtn = this.querySelector('[data-ea-next]');
    this._mainArea = this.querySelector('[data-ea-main]') || this._slidesContainer;

    if (!this._slidesContainer) {
      warn('No slides container found; aborting init.');
      return;
    }

    this._slides = Array.prototype.slice.call(
      this._slidesContainer.querySelectorAll('.ea-gallery__slide')
    );
    this._thumbs = this._thumbsContainer
      ? Array.prototype.slice.call(this._thumbsContainer.querySelectorAll('.ea-gallery__thumb'))
      : [];

    // Build a set of variant IDs we own
    this._ownedVariantIds = {};
    var self = this;
    this._slides.forEach(function (slide) {
      var vid = slide.dataset.eaVariantId;
      if (vid) self._ownedVariantIds[String(vid)] = true;
    });

    this._isQuickView = this.dataset.quickView === 'true';
    this._sectionId = this.dataset.sectionId || null;

    log('Initializing', {
      slideCount: this._slides.length,
      thumbCount: this._thumbs.length,
      device: this.dataset.device,
      sectionId: this._sectionId,
      isQuickView: this._isQuickView,
      ownedVariantIds: Object.keys(this._ownedVariantIds)
    });

    // Bind handlers
    this._onThumbClick = this._onThumbClick.bind(this);
    this._onPrevClick = this._onPrevClick.bind(this);
    this._onNextClick = this._onNextClick.bind(this);
    this._onVariantChange = this._onVariantChange.bind(this);
    this._onMainAreaClick = this._onMainAreaClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);

    if (this._thumbsContainer) {
      this._thumbsContainer.addEventListener('click', this._onThumbClick);
    }
    if (this._prevBtn) this._prevBtn.addEventListener('click', this._onPrevClick);
    if (this._nextBtn) this._nextBtn.addEventListener('click', this._onNextClick);

    if (this._mainArea) {
      // Click anywhere on the main image area opens the lightbox.
      // (Arrows live inside the main area and stop propagation themselves.)
      this._mainArea.addEventListener('click', this._onMainAreaClick);
      // Keep the main area focusable so keyboard left/right still navigates slides
      if (!this._mainArea.hasAttribute('tabindex')) {
        this._mainArea.setAttribute('tabindex', '0');
      }
      this._mainArea.addEventListener('keydown', this._onKeyDown);
    }

    // Initial filter based on selected variant
    var initialVariantId = this.dataset.selectedVariantId;
    this._currentVariantId = initialVariantId;
    this._filterToVariant(initialVariantId);

    // Subscribe to global variant change events
    whenEventBusReady(function () {
      if (window.eventBus && typeof window.eventBus.on === 'function') {
        window.eventBus.on('variant:change', self._onVariantChange);
        log('Subscribed to variant:change events');
      }
    });
  };

  EAProductGallery.prototype.disconnectedCallback = function () {
    if (window.eventBus && typeof window.eventBus.off === 'function' && this._onVariantChange) {
      window.eventBus.off('variant:change', this._onVariantChange);
    }
  };

  // ---- Filter logic ----
  EAProductGallery.prototype._filterToVariant = function (variantId) {
    if (variantId == null) return;
    var idStr = String(variantId);
    var visibleSlides = [];

    this._slides.forEach(function (slide) {
      var slideVariantId = slide.dataset.eaVariantId;
      // Clear the first-of-variant marker on every slide; we'll re-stamp it
      // on the first matching slide below. This drives the badge CSS rule
      // that shows the badge only on the first image of the current variant.
      slide.removeAttribute('data-ea-first-of-variant');
      if (String(slideVariantId) === idStr) {
        slide.classList.add('is-current-variant');
        slide.classList.remove('is-active');
        visibleSlides.push(slide);
      } else {
        slide.classList.remove('is-current-variant');
        slide.classList.remove('is-active');
      }
    });

    this._thumbs.forEach(function (thumb) {
      var thumbVariantId = thumb.dataset.eaVariantId;
      if (String(thumbVariantId) === idStr) {
        thumb.classList.add('is-current-variant');
        thumb.classList.remove('is-active');
      } else {
        thumb.classList.remove('is-current-variant');
        thumb.classList.remove('is-active');
      }
    });

    if (visibleSlides.length > 0) {
      visibleSlides[0].setAttribute('data-ea-first-of-variant', 'true');
      var firstSlideId = visibleSlides[0].dataset.eaSlideId;
      this._activateSlide(firstSlideId);
    }

    var hasMultiple = visibleSlides.length > 1;
    if (this._prevBtn) this._prevBtn.hidden = !hasMultiple;
    if (this._nextBtn) this._nextBtn.hidden = !hasMultiple;

    log('Filtered to variant', idStr, 'visible slides:', visibleSlides.length);

    if (visibleSlides.length === 0) {
      warn('No slides matched variant id ' + idStr + '.');
    }
  };

  EAProductGallery.prototype._activateSlide = function (slideId) {
    if (!slideId) return;
    this._slides.forEach(function (slide) {
      if (slide.dataset.eaSlideId === slideId) {
        slide.classList.add('is-active');
      } else {
        slide.classList.remove('is-active');
      }
    });
    this._thumbs.forEach(function (thumb) {
      if (thumb.dataset.eaThumbFor === slideId) {
        thumb.classList.add('is-active');
      } else {
        thumb.classList.remove('is-active');
      }
    });
  };

  // ---- Accessor used by the lightbox ----
  EAProductGallery.prototype._getActiveSlide = function () {
    for (var i = 0; i < this._slides.length; i++) {
      if (this._slides[i].classList.contains('is-current-variant') &&
          this._slides[i].classList.contains('is-active')) {
        return this._slides[i];
      }
    }
    return null;
  };

  // ---- Click handlers ----
  EAProductGallery.prototype._onThumbClick = function (e) {
    var thumb = e.target.closest('.ea-gallery__thumb');
    if (!thumb) return;
    if (!thumb.classList.contains('is-current-variant')) return;
    e.preventDefault();
    var slideId = thumb.dataset.eaThumbFor;
    this._activateSlide(slideId);
  };

  EAProductGallery.prototype._getCurrentSlideIndex = function () {
    var visible = this._slides.filter(function (s) {
      return s.classList.contains('is-current-variant');
    });
    for (var i = 0; i < visible.length; i++) {
      if (visible[i].classList.contains('is-active')) return { index: i, list: visible };
    }
    return { index: 0, list: visible };
  };

  EAProductGallery.prototype._goPrev = function () {
    var state = this._getCurrentSlideIndex();
    if (state.list.length === 0) return;
    var nextIdx = (state.index - 1 + state.list.length) % state.list.length;
    this._activateSlide(state.list[nextIdx].dataset.eaSlideId);
  };

  EAProductGallery.prototype._goNext = function () {
    var state = this._getCurrentSlideIndex();
    if (state.list.length === 0) return;
    var nextIdx = (state.index + 1) % state.list.length;
    this._activateSlide(state.list[nextIdx].dataset.eaSlideId);
  };

  EAProductGallery.prototype._onPrevClick = function (e) {
    e.preventDefault();
    e.stopPropagation();
    this._goPrev();
  };

  EAProductGallery.prototype._onNextClick = function (e) {
    e.preventDefault();
    e.stopPropagation();
    this._goNext();
  };

  // ---- Main image click → open lightbox ----
  EAProductGallery.prototype._onMainAreaClick = function (e) {
    // Ignore clicks on arrows or any nested interactive control
    if (e.target.closest('[data-ea-prev], [data-ea-next], a, button')) return;
    var state = this._getCurrentSlideIndex();
    if (state.list.length === 0) return;
    getLightbox().open(this);
  };

  // ---- Keyboard nav (slide-level, when main area has focus) ----
  EAProductGallery.prototype._onKeyDown = function (e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this._goPrev();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this._goNext();
    } else if (e.key === 'Enter' || e.key === ' ') {
      // Activate the focused main area as an accessible "open lightbox"
      e.preventDefault();
      var state = this._getCurrentSlideIndex();
      if (state.list.length === 0) return;
      getLightbox().open(this);
    }
  };

  // ---- Variant change handler ----
  EAProductGallery.prototype._onVariantChange = function (payload) {
    if (!payload || !payload.variant || !payload.variant.id) return;

    var newVariantId = String(payload.variant.id);

    if (
      payload.sectionId &&
      this._sectionId &&
      payload.sectionId === this._sectionId
    ) {
      log('variant:change matched by sectionId', payload.sectionId);
      this._applyVariantChange(newVariantId);
      return;
    }

    if (this._ownedVariantIds[newVariantId]) {
      log('variant:change matched by variant ownership', {
        eventSection: payload.sectionId,
        gallerySection: this._sectionId,
        variantId: newVariantId
      });
      this._applyVariantChange(newVariantId);
      return;
    }

    log('variant:change ignored', {
      eventSection: payload.sectionId,
      gallerySection: this._sectionId,
      variantId: newVariantId
    });
  };

  EAProductGallery.prototype._applyVariantChange = function (newVariantId) {
    if (String(newVariantId) === String(this._currentVariantId)) return;
    this._currentVariantId = newVariantId;
    this._filterToVariant(newVariantId);
    // If the lightbox is currently open against this gallery, refresh the image
    if (_lightbox && _lightbox.el.classList.contains('is-open')) {
      _lightbox.refresh();
    }
  };

  customElements.define('ea-product-gallery', EAProductGallery);
  log('Custom element registered');

  // ---------------------------------------------------------
  // QUICK-VIEW UPGRADE FALLBACK (unchanged)
  // ---------------------------------------------------------
  function ensureInitialized(el) {
    if (!el || el._initialized) return;
    setTimeout(function () {
      if (el._initialized) return;
      log('Force-initializing un-upgraded gallery instance', el);
      try {
        EAProductGallery.prototype.connectedCallback.call(el);
      } catch (err) {
        warn('Force-init failed:', err);
      }
    }, 50);
  }

  function sweepExisting() {
    var galleries = document.querySelectorAll('ea-product-gallery');
    galleries.forEach(ensureInitialized);
  }

  function startObserver() {
    if (!window.MutationObserver) return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'EA-PRODUCT-GALLERY') {
            ensureInitialized(node);
          }
          if (node.querySelectorAll) {
            var nested = node.querySelectorAll('ea-product-gallery');
            for (var k = 0; k < nested.length; k++) {
              ensureInitialized(nested[k]);
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    log('MutationObserver watching for new gallery instances');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      sweepExisting();
      startObserver();
    });
  } else {
    sweepExisting();
    startObserver();
  }
})();
